import { useEffect, useMemo, useRef, useState } from "preact/hooks";
import { BetAvatar } from "../../shared/ui";
import { useBetFeedStore } from "../../store/betFeedStore";
import css from "./UserCashOut.module.scss";
import { useTransitionList } from "./useTransitionList";

interface UserCashOutBarProp {
  avatar: string;
  name: string;
  multiplayer: number | string;
  payout?: number | string;
}
export interface CashoutItem {
  id: string;
  avatar: string;
  name: string;
  multiplayer: number | string;
  payout?: number | string;
  isBigWin?: boolean;
}

const MAX_VISIBLE = 5;
const DISPLAY_MS = 1500;

export const UserCashOutBar = ({
  avatar,
  name,
  multiplayer,
}: UserCashOutBarProp) => (
  <div className={css.container}>
    <BetAvatar size={22} avatar={`${avatar}.png`} className={css.icon} />
    <div className={css.secondCol}>
      <p>{name}</p>
      <p>{Number(multiplayer).toFixed(2)}x</p>
    </div>
  </div>
);
const UserCashOutBarBigWin = ({
  avatar,
  name,
  multiplayer,
  payout,
}: UserCashOutBarProp) => (
  <div className={css.containerBig}>
    <BetAvatar
      size={30}
      avatar={`${avatar}.png`}
      className={css.icon}
      classNameAvatar={css.avatarBig}
    />
    <div className={css.secondCol}>
      <div className={css.firstRow}>
        <p>WIN</p>
        <p>{Number(payout).toFixed(2)}$</p>
      </div>
      <div className={css.secondRow}>
        <p>{name}</p>
        <p>{Number(multiplayer).toFixed(2)}x</p>
      </div>
    </div>
  </div>
);

const AvatarStack = ({ avatars }: { avatars: string[] }) => {
  return (
    <div className={css.avatarsStack}>
      {avatars.slice(0, 3).map((a, i) => (
        <BetAvatar
          size={24}
          key={a + i}
          avatar={`${a}.png`}
          className={css.stackAvatar}
        />
      ))}
    </div>
  );
};

const ParticipantsBadge = ({ count }: { count: number }) => (
  <div className={css.participantsCount}>
    <p>{count}</p>
  </div>
);

export const UserCashOutContainer = () => {
  const [localCashouts, setLocalCashouts] = useState<CashoutItem[]>([]);

  const { currentBets, playersBettingCounters } = useBetFeedStore();

  const shownRef = useRef<Set<string>>(new Set());
  const timersRef = useRef<Record<string, number>>({});
  const visibleRef = useRef<CashoutItem[]>([]);

  const items = useTransitionList(localCashouts, (c) => c.id, {
    durationMs: 1000,
    addOnTop: true,
  });

  const stackedAvatars = useMemo(
    () => currentBets.map((e) => e.avatar).slice(0, 3),
    [currentBets]
  );

  useEffect(() => {
    visibleRef.current = localCashouts;
  }, [localCashouts]);

  useEffect(() => {
    for (const event of currentBets) {
      const hasResult =
        (event.multiplier ?? 0) !== 0 || (event.payout ?? 0) !== 0;
      if (!hasResult) continue;
      if (shownRef.current.has(event.id)) continue;
      shownRef.current.add(event.id);

      if (visibleRef.current.length >= MAX_VISIBLE) {
        continue;
      }

      const item: CashoutItem = {
        id: event.id,
        avatar: event.avatar,
        name: event.nickname,
        multiplayer: (event.multiplier ?? 0) as number,
        isBigWin: event.isBigWin ?? false,
        payout: event.payout ?? 0,
      };

      setLocalCashouts((prev) => {
        const updated = [item, ...prev];
        visibleRef.current = updated;
        return updated;
      });

      const t = window.setTimeout(() => {
        setLocalCashouts((prev) => {
          const filtered = prev.filter((x) => x.id !== item.id);
          visibleRef.current = filtered;
          return filtered;
        });
        if (timersRef.current[item.id]) {
          window.clearTimeout(timersRef.current[item.id]);
          delete timersRef.current[item.id];
        }
      }, DISPLAY_MS);
      timersRef.current[item.id] = t;
    }
  }, [currentBets]);

  useEffect(() => {
    return () => {
      Object.values(timersRef.current).forEach((t) => window.clearTimeout(t));
      timersRef.current = {};
      shownRef.current.clear();
      visibleRef.current = [];
    };
  }, []);
  if (playersBettingCounters.activeBetsCount === 0) return null;

  return (
    <div className={css.wrapper}>
      <div className={css.topBar}>
        <AvatarStack avatars={stackedAvatars} />
        <ParticipantsBadge
          count={playersBettingCounters.activeBetsCount ?? 0}
        />
      </div>

      <div className={css.list}>
        {items.map((it) => (
          <div
            key={it.id}
            className={`${css.listItem} ${it.state === "enter" ? css.enter : ""} ${it.state === "leave" ? css.leave : ""}`}
          >
            {it.isBigWin ? (
              <UserCashOutBarBigWin
                avatar={it.avatar}
                name={it.name}
                multiplayer={it.multiplayer}
                payout={it.payout}
              />
            ) : (
              <UserCashOutBar
                avatar={it.avatar}
                name={it.name}
                multiplayer={it.multiplayer}
              />
            )}
          </div>
        ))}
      </div>
    </div>
  );
};
